import React, { useEffect, useRef, useState } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const BRAND_BROWN = "#674934";
const ENGAGE_RATIO = 0.7;
const RELEASE_RATIO = 0.5;
const COOLDOWN_MS = 400;
const SETTLE_MS = 150;

function getVisibleRatio(el) {
	if (!el) return 0;
	const rect = el.getBoundingClientRect();
	const vh = window.innerHeight || document.documentElement.clientHeight;
	const visible = Math.max(
		0,
		Math.min(rect.bottom, vh) - Math.max(rect.top, 0)
	);
	return visible / Math.max(1, rect.height);
}

function getScrollbarWidth() {
	const outer = document.createElement("div");
	outer.style.visibility = "hidden";
	outer.style.overflow = "scroll";
	outer.style.msOverflowStyle = "scrollbar";
	document.body.appendChild(outer);

	const inner = document.createElement("div");
	outer.appendChild(inner);

	const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

	outer.parentNode.removeChild(outer);
	return scrollbarWidth;
}

export default function ProcessSectionMUI() {
	const processSlides = [
		{
			title: "BRIEFING",
			description:
				"We begin by understanding your style, preferences, and goals. This allows us to align our vision with your personality and lifestyle.",
			image: "/images/our-process/briefing.png",
		},
		{
			title: "DESIGN",
			description:
				"Our creative team develops concepts and moodboards that capture your expectations and translate them into clear plans.",
			image: "/images/our-process/design.jpg",
		},
		{
			title: "EXECUTION",
			description:
				"With precision and quality craftsmanship, our skilled team brings the approved designs to life using top-grade materials.",
			image: "/images/our-process/execution.png",
		},
		{
			title: "HANDOVER",
			description:
				"We finalize every detail and hand over a polished, functional space—ready for you to enjoy.",
			image: "/images/our-process/handover.png",
		},
	];

	const [activeIndex, setActiveIndex] = useState(0);
	const [released, setReleased] = useState(true);
	const containerRef = useRef(null);

	// NEW: keep activeIndex in a ref so handlers always see latest value
	const activeIndexRef = useRef(0);
	useEffect(() => {
		activeIndexRef.current = activeIndex;
	}, [activeIndex]);

	// NEW: keep a ref in sync with released
	const releasedRef = useRef(released);
	useEffect(() => {
		releasedRef.current = released;
	}, [released]);

	const [scrollbarWidth, setScrollbarWidth] = useState(null);

	const isSectionActiveRef = useRef(false);
	const [isSectionActive, setIsSectionActive] = useState(false);
	const isAnimatingRef = useRef(false);
	const isSettlingRef = useRef(false);

	// 1. Calculate and store scrollbar width on mount
	useEffect(() => {
		setScrollbarWidth(getScrollbarWidth());
	}, []);

	// 2. Visibility with hysteresis (no velocity logic now)
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		const thresholds = Array.from({ length: 101 }, (_, i) => i / 100);

		const obs = new IntersectionObserver(
			([entry]) => {
				const ratio = entry.intersectionRatio;

				// Engage when enough of the section is visible
				if (!isSectionActiveRef.current && ratio >= ENGAGE_RATIO) {
					isSectionActiveRef.current = true;
					setIsSectionActive(true);
					setReleased(false);
					isSettlingRef.current = true;
					setTimeout(() => (isSettlingRef.current = false), SETTLE_MS);
				}

				// Release when it is mostly out of view
				if (ratio <= RELEASE_RATIO) {
					isSectionActiveRef.current = false;
					setIsSectionActive(false);
					setReleased(true);
					document.body.style.overflow = "auto";
					document.body.style.paddingRight = "";
				}
			},
			{ threshold: thresholds }
		);

		obs.observe(el);
		return () => {
			obs.disconnect();
		};
	}, []);

	// Touch swipe (mobile) – capture gesture on the section itself
	// Touch swipe (mobile) – capture gesture on the section itself
	useEffect(() => {
		const sectionEl = containerRef.current;
		if (!sectionEl) return;

		let startY = 0;
		let lastY = 0;

		const onTouchStart = (e) => {
			startY = e.touches[0].clientY;
			lastY = startY;
		};

		const onTouchMove = (e) => {
			const currentY = e.touches[0].clientY;
			const deltaY = currentY - lastY;
			lastY = currentY;

			const dir = Math.sign(-deltaY); // swipe up => dir > 0 (go "down" in steps)
			if (dir === 0) return;

			const index = activeIndexRef.current; // <-- live index
			const lastIndex = processSlides.length - 1;
			const ratio = getVisibleRatio(sectionEl);

			// --- If section NOT active yet, decide whether to engage ---
			if (!isSectionActiveRef.current) {
				// If user is at top and scrolling up (towards page top),
				// or at bottom and scrolling down (towards page bottom),
				// they are trying to LEAVE, so do not engage; let page scroll.
				if ((index === 0 && dir < 0) || (index === lastIndex && dir > 0)) {
					return; // no preventDefault -> page scrolls
				}

				// Otherwise we can engage if visible enough and not in "released" mode
				if (ratio >= ENGAGE_RATIO && !releasedRef.current) {
					isSectionActiveRef.current = true;
					setIsSectionActive(true);
					setReleased(false);
					isSettlingRef.current = true;
					setTimeout(() => (isSettlingRef.current = false), SETTLE_MS);
				} else {
					// Not visible enough or we are in released mode -> let page scroll
					return;
				}
			}

			// If released, never hijack; let page scroll
			if (releasedRef.current) return;

			const atTop = index === 0;
			const atBottom = index === lastIndex;

			// --- Already active, but user is at boundary and trying to leave ---
			if ((atTop && dir < 0) || (atBottom && dir > 0)) {
				releasedRef.current = true;
				setReleased(true);
				isSectionActiveRef.current = false;
				setIsSectionActive(false);
				return; // no preventDefault -> THIS gesture scrolls the page
			}

			// --- Internal step: hijack scroll and move between slides ---
			e.preventDefault();

			if (isSettlingRef.current || isAnimatingRef.current) return;

			const nextIndex = index + dir;
			if (nextIndex < 0 || nextIndex > lastIndex) return;

			isAnimatingRef.current = true;
			activeIndexRef.current = nextIndex; // keep ref in sync immediately
			setActiveIndex(nextIndex);

			setTimeout(() => {
				isAnimatingRef.current = false;
			}, COOLDOWN_MS);
		};

		sectionEl.addEventListener("touchstart", onTouchStart, { passive: true });
		sectionEl.addEventListener("touchmove", onTouchMove, { passive: false });

		return () => {
			sectionEl.removeEventListener("touchstart", onTouchStart);
			sectionEl.removeEventListener("touchmove", onTouchMove);
		};
		// note: no activeIndex in deps; we use refs instead
	}, [processSlides.length]);

	// Clean up animation flags when section becomes inactive
	useEffect(() => {
		if (!isSectionActive) {
			isAnimatingRef.current = false;
			isSettlingRef.current = false;
		}
	}, [isSectionActive]);

	// Force release on escape key
	useEffect(() => {
		const forceRelease = () => {
			setReleased(true);
			isSectionActiveRef.current = false;
			setIsSectionActive(false);
			isAnimatingRef.current = false;
			isSettlingRef.current = false;
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
		};

		const onKeyDown = (e) => {
			if (e.key === "Escape") forceRelease();
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, []);

	// Wheel handler (desktop)
	useEffect(() => {
		const onWheel = (e) => {
			if (released) return;
			if (!isSectionActiveRef.current) return;

			const ratio = getVisibleRatio(containerRef.current);
			if (ratio < ENGAGE_RATIO) return;

			const dir = Math.sign(e.deltaY);
			const nextIndex = activeIndex + dir;

			// Boundary: at start/end, let the page scroll normally
			if (
				(activeIndex === processSlides.length - 1 && dir > 0) ||
				(activeIndex === 0 && dir < 0)
			) {
				setReleased(true);
				isSectionActiveRef.current = false;
				setIsSectionActive(false);
				return;
			}

			// Internal step: hijack scroll to move between process slides
			if (nextIndex >= 0 && nextIndex < processSlides.length) {
				e.preventDefault();
				if (isSettlingRef.current || isAnimatingRef.current) return;

				isAnimatingRef.current = true;
				setActiveIndex(nextIndex);

				setTimeout(() => {
					isAnimatingRef.current = false;
				}, COOLDOWN_MS);
			}
		};

		window.addEventListener("wheel", onWheel, { passive: false });
		return () =>
			window.removeEventListener("wheel", onWheel, { passive: false });
	}, [activeIndex, processSlides.length, released]);

	// Touch swipe (mobile) – capture gesture on the section itself
	useEffect(() => {
		const sectionEl = containerRef.current;
		if (!sectionEl) return;

		let startY = 0;
		let lastY = 0;

		const onTouchStart = (e) => {
			startY = e.touches[0].clientY;
			lastY = startY;
		};

		const onTouchMove = (e) => {
			const currentY = e.touches[0].clientY;
			const deltaY = currentY - lastY;
			lastY = currentY;

			const dir = Math.sign(-deltaY); // swipe up => dir > 0 (go "down" through steps)
			if (dir === 0) return;

			const lastIndex = processSlides.length - 1;
			const ratio = getVisibleRatio(sectionEl);

			// --- If section is NOT active yet, decide whether to engage ---
			if (!isSectionActiveRef.current) {
				// If we are at the top and user scrolls UP (dir < 0),
				// or at the bottom and user scrolls DOWN (dir > 0),
				// user is trying to LEAVE the section. Do NOT engage; let page scroll.
				if (
					(activeIndex === 0 && dir < 0) ||
					(activeIndex === lastIndex && dir > 0)
				) {
					return; // no preventDefault -> page scrolls
				}

				// Otherwise, if enough of the section is visible, engage it
				if (ratio >= ENGAGE_RATIO && !releasedRef.current) {
					isSectionActiveRef.current = true;
					setIsSectionActive(true);
					setReleased(false);
					isSettlingRef.current = true;
					setTimeout(() => (isSettlingRef.current = false), SETTLE_MS);
				} else {
					// not visible enough -> normal page scroll
					return;
				}
			}

			// If we get here and we are released, just let page scroll
			if (releasedRef.current) return;

			const atTop = activeIndex === 0;
			const atBottom = activeIndex === lastIndex;

			// --- Already active, but user is at boundary and trying to leave ---
			if ((atTop && dir < 0) || (atBottom && dir > 0)) {
				releasedRef.current = true;
				setReleased(true);
				isSectionActiveRef.current = false;
				setIsSectionActive(false);
				return; // no preventDefault -> this same gesture scrolls the page
			}

			// --- Internal step: hijack scroll to move between slides ---
			e.preventDefault();

			if (isSettlingRef.current || isAnimatingRef.current) return;

			const nextIndex = activeIndex + dir;
			if (nextIndex < 0 || nextIndex > lastIndex) return;

			isAnimatingRef.current = true;
			setActiveIndex(nextIndex);

			setTimeout(() => {
				isAnimatingRef.current = false;
			}, COOLDOWN_MS);
		};

		sectionEl.addEventListener("touchstart", onTouchStart, { passive: true });
		sectionEl.addEventListener("touchmove", onTouchMove, { passive: false });

		return () => {
			sectionEl.removeEventListener("touchstart", onTouchStart);
			sectionEl.removeEventListener("touchmove", onTouchMove);
		};
	}, [activeIndex, processSlides.length]);

	// ---------- LAYOUT ----------
	return (
		<Box
			ref={containerRef}
			sx={{
				width: "100vw",
				maxWidth: "100%",
				py: { xs: 3, md: 4 },
				backgroundColor: BRAND_BROWN,
				marginRight: "calc(-1 * (100vw - 100%))",
				paddingRight: "calc(100vw - 100%)",
				touchAction: isSectionActive && !released ? "none" : "auto",
			}}
		>
			<Container
				maxWidth={false}
				sx={{ width: { xs: "95%", md: "min(1500px, 95%)" } }}
			>
				{/* Header */}
				<Box sx={{ textAlign: "center", mb: { xs: 4, md: 4 } }}>
					<Typography
						fontFamily="'Source Sans Pro', system-ui, sans-serif"
						fontWeight={300}
						fontSize={14}
						letterSpacing={2}
						color="#fff"
					>
						OUR PROCESS
					</Typography>
					<Typography
						component="h2"
						fontFamily="'Lora', serif"
						fontWeight={500}
						fontSize={{ xs: 32, md: 44 }}
						lineHeight={1.1}
						color="#fff"
						sx={{ mt: 1 }}
					>
						Our Proven Design Approach
					</Typography>
					<Typography
						fontFamily="'Source Sans Pro', system-ui, sans-serif"
						fontWeight={400}
						fontSize={18}
						color="#fff"
						sx={{ mt: 1, opacity: 0.9 }}
					>
						Collaborative, Efficient, and Tailored to Your Needs
					</Typography>
				</Box>

				{/* Main: rail | right content */}
				<Grid
					container
					spacing={{ xs: 3, md: 3 }}
					alignItems="stretch"
					columns={12}
				>
					{/* LEFT RAIL */}
					<Grid size={{ xs: 3, md: 3 }}>
						<Box
							sx={{
								height: "100%",
								display: "grid",
								gridTemplateRows: "auto 0.7fr auto 0.7fr auto 0.7fr auto",
								alignItems: "start",
								pl: { md: 9, lg: 20, xl: 20 },
							}}
						>
							{processSlides.map((slide, i) => (
								<React.Fragment key={slide.title}>
									<Box
										sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
									>
										<Box
											sx={{
												width: 14,
												height: 14,
												borderRadius: "50%",
												border: "2px solid #fff",
												backgroundColor:
													activeIndex === i ? "#fff" : "transparent",
												transition: "background-color .25s ease",
											}}
										/>
										<Typography
											fontFamily="'Source Sans Pro', system-ui, sans-serif"
											fontWeight={600}
											fontSize={{ xs: 14, sm: 15, md: 16 }}
											letterSpacing={1}
											color="#fff"
											sx={{
												opacity: activeIndex === i ? 1 : 0.85,
												whiteSpace: "nowrap",
												["@media (max-width:460px)"]: {
													fontSize: 12,
													letterSpacing: 0.5,
												},
											}}
										>
											{slide.title}
										</Typography>
									</Box>

									{i < processSlides.length - 1 && (
										<Box
											sx={{
												ml: "7px",
												borderLeft: "2px dotted rgba(255,255,255,0.9)",
												width: 0,
												height: "100%",
											}}
										/>
									)}
								</React.Fragment>
							))}
						</Box>
					</Grid>

					{/* RIGHT CONTENT */}
					<Grid size={{ xs: 9, md: 9 }}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", md: "5fr 4fr" },
								columnGap: { md: 6 },
								rowGap: { xs: 3, md: 0 },
								alignItems: "start",
								height: "100%",
								["@media (max-width:460px)"]: { pl: 1 },
							}}
						>
							{/* IMAGE */}
							<Box
								sx={{
									gridColumn: { xs: "1", md: "1" },
									gridRow: { xs: "2", md: "1" },
									alignSelf: "start",
									pl: { md: 9, lg: 16, xl: 16 },
								}}
							>
								<Box
									component="img"
									src={processSlides[activeIndex].image}
									alt={processSlides[activeIndex].title}
									sx={{
										width: "100%",
										maxWidth: { xs: 300, sm: 360, md: "unset" },
										borderRadius: 2,
										boxShadow: "0 6px 24px rgba(0,0,0,.25)",
										objectFit: "cover",
										display: "block",
										["@media (max-width:460px)"]: { maxWidth: 280 },
									}}
								/>
							</Box>

							{/* DESCRIPTION */}
							<Box
								sx={{
									gridColumn: { xs: "1", md: "2" },
									gridRow: { xs: "1", md: "1" },
									pl: { md: 0, lg: 2, xl: 4 },
								}}
							>
								<Typography
									fontFamily="'Source Sans Pro', system-ui, sans-serif"
									fontWeight={400}
									fontSize={{ xs: 15, md: 18 }}
									lineHeight={1.6}
									color="#fff"
								>
									{processSlides[activeIndex].description}
								</Typography>
							</Box>
						</Box>
					</Grid>
				</Grid>
			</Container>
		</Box>
	);
}
